import { download, generateCsv, mkConfig } from "export-to-csv"
import { DateTime } from "luxon"
import { useCallback } from "react"
import { useDateTime } from "../../../../localization/useDateTime"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { BookingSummaryEntry } from "../statisticsTypes"

type useExportBookingsDurationToCsvParams = Readonly<{
    data: BookingSummaryEntry[] | null | undefined
    startDate: DateTime
    endDate: DateTime
}>

export function useExportBookingsDuration({ data, startDate, endDate }: useExportBookingsDurationToCsvParams) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-bookings-duration-${dateTimeFactory.formatDate(startDate)}_${dateTimeFactory.formatDate(
            endDate,
        )}`,
    })

    const mapEntryToCsvRow = (entry: BookingSummaryEntry) => {
        return {
            From: dateTimeFactory.formatDate(startDate),
            To: dateTimeFactory.formatDate(endDate),
            Resource: entry.resource.displayName,
            "Bookings Amount": entry.numberOfBookings,
            "Average Duration": dateTimeFactory.formatDuration(
                dateTimeFactory.fromDurationString(entry.averageDuration),
            ),
        }
    }

    return useCallback(() => {
        let exportRows: ReturnType<typeof mapEntryToCsvRow>[]

        if (data && data.length > 0) {
            exportRows = data.map((entry) => mapEntryToCsvRow(entry))
        } else {
            exportRows = [mapEntryToCsvRow({} as BookingSummaryEntry)]
        }

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, data, startDate, endDate, l10n])
}
